import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import type { UseFormReturn } from 'react-hook-form';
import { useSignupControllerSignupMutation } from '@/services/generated/api';
import { isFetchBaseQueryError } from '@/services/baseApi';
import { CenteredCardLayout } from '@/components/ui/centered-card-layout';
import { Form } from '@/components/ui/form';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';

/**
 * The TCIT brand above the card (the same pattern as `TenantSelectPage`: plain
 * text with semantic roles, no logo asset in the repo).
 */
const TCIT_BRAND = <span className="text-h3 font-extrabold text-fg">TCIT</span>;

/**
 * View 2 (§4.1 / §7.2 of the inventory): self-serve organisation sign-up, with
 * no prior session. The real endpoint: `POST /api/signup` ->
 * `SignupControllerSignupApiArg`/`SignupResponseDto` (codegen,
 * `services/generated/api.ts`) — types are NOT declared by hand.
 *
 * `SignupDto` (the real DTO, `src/modules/tenants/dto/signup.dto.ts` in the
 * backend) carries no RUT: only `organizationName`, `email` and an optional
 * `password`. So this view uses no `RutInput` — the brief does not ask for one
 * and the contract does not allow one either, there is no field to fill with it.
 *
 * `organizationSlug` is gone from the form on purpose. It is a technical
 * identifier the buyer has no reason to have an opinion about, and its 409
 * landed on a field they did not know they were choosing; the backend derives
 * it from the name now. See `docs/DIGITAL_ONBOARDING.md` §2 and
 * `src/modules/tenants/slug.ts`.
 */

const signupSchema = z.object({
  organizationName: z
    .string()
    .trim()
    .min(2, 'Ingresa al menos 2 caracteres.')
    .max(100, 'No puede superar los 100 caracteres.'),
  email: z
    .string()
    .trim()
    .min(1, 'Ingresa un correo electrónico.')
    .email('Ingresa un correo electrónico válido.')
    .max(120, 'No puede superar los 120 caracteres.'),
  // Optional in the real DTO: left blank, the backend generates a password for
  // the SSO account (best-effort, it never blocks the sign-up). It is validated
  // as a plain string (not `.optional()`) because an untouched text field
  // arrives as `''`, not as `undefined` — the "they did not fill it in"
  // condition is resolved in the `refine`, not in the type's shape.
  password: z
    .string()
    .max(72, 'La contraseña no puede superar los 72 caracteres.')
    .refine((value) => value.length === 0 || value.length >= 8, {
      message: 'Debe tener al menos 8 caracteres.',
    }),
});

type SignupFormValues = z.infer<typeof signupSchema>;

const SIGNUP_DEFAULT_VALUES: SignupFormValues = {
  organizationName: '',
  email: '',
  password: '',
};

/**
 * Maps this backend's 400/409 to per-field errors. See `serverErrors`'s JSDoc
 * in `components/ui/form.tsx`: Nest's `ValidationPipe` (mounted with no
 * `exceptionFactory` of its own, `src/configure-app.ts`) answers
 * `{ statusCode: 400, message: string[] }`, one English message per failing
 * `class-validator` rule, not per field.
 *
 * Rather than guessing the field from each sentence's first word (a generic,
 * brittle heuristic), each message is compared against the EXACT patterns the
 * validators declared in `src/modules/tenants/dto/signup.dto.ts` produce — this
 * file owns that endpoint and can afford to know it that literally. A message
 * that matches no known pattern is NOT assigned to any field (it would be
 * reported on the wrong field, which is worse than not reporting it): it falls
 * through to the general error.
 */
const SIGNUP_FIELD_ERROR_PATTERNS: ReadonlyArray<{
  field: keyof SignupFormValues;
  pattern: RegExp;
  message: string;
}> = [
  {
    field: 'organizationName',
    pattern: /^organizationName must be longer than or equal to \d+ characters$/,
    message: 'Ingresa al menos 2 caracteres.',
  },
  {
    field: 'organizationName',
    pattern: /^organizationName must be shorter than or equal to \d+ characters$/,
    message: 'No puede superar los 100 caracteres.',
  },
  {
    field: 'organizationName',
    pattern: /^organizationName must be a string$/,
    message: 'Ingresa un nombre de organización válido.',
  },
  {
    field: 'email',
    pattern: /^email must be an email$/,
    message: 'Ingresa un correo electrónico válido.',
  },
  {
    field: 'email',
    pattern: /^email must be shorter than or equal to \d+ characters$/,
    message: 'No puede superar los 120 caracteres.',
  },
  {
    field: 'password',
    pattern: /^password must be longer than or equal to \d+ characters$/,
    message: 'Debe tener al menos 8 caracteres.',
  },
  {
    field: 'password',
    pattern: /^password must be shorter than or equal to \d+ characters$/,
    message: 'No puede superar los 72 caracteres.',
  },
  {
    field: 'password',
    pattern: /^password must be a string$/,
    message: 'Ingresa una contraseña válida.',
  },
];

interface SignupSubmitErrorMapping {
  fieldErrors: Record<string, string> | null;
  generalMessage: string | null;
}

const GENERIC_SUBMIT_ERROR =
  'No se pudo completar el registro. Verifica los datos e inténtalo de nuevo.';
const SERVER_SUBMIT_ERROR =
  'Ocurrió un problema en el servidor. Inténtalo de nuevo en unos minutos.';

function hasArrayMessage(data: unknown): data is { message: string[] } {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { message?: unknown }).message)
  );
}

function mapSignupSubmitError(error: unknown): SignupSubmitErrorMapping {
  if (!error) {
    return { fieldErrors: null, generalMessage: null };
  }

  if (!isFetchBaseQueryError(error)) {
    // Redux's `SerializedError`, or any other shape: there is no HTTP status to
    // map.
    return { fieldErrors: null, generalMessage: GENERIC_SUBMIT_ERROR };
  }

  const { status, data } = error;

  // 409 no longer lands on a field this form shows: the slug is derived, and a
  // derived collision is resolved with a suffix instead of reported. It can
  // still arrive from an older client that sent its own slug, so it keeps a
  // general message rather than pointing at an input that is not here.
  if (status === 409) {
    return { fieldErrors: null, generalMessage: GENERIC_SUBMIT_ERROR };
  }

  if (status === 400 && hasArrayMessage(data)) {
    const fieldErrors: Record<string, string> = {};
    let hasUnmatched = false;

    for (const raw of data.message) {
      const match = SIGNUP_FIELD_ERROR_PATTERNS.find(({ pattern }) => pattern.test(raw));
      if (match) {
        fieldErrors[match.field] = fieldErrors[match.field]
          ? `${fieldErrors[match.field]} ${match.message}`
          : match.message;
      } else {
        hasUnmatched = true;
      }
    }

    const hasFieldErrors = Object.keys(fieldErrors).length > 0;
    return {
      fieldErrors: hasFieldErrors ? fieldErrors : null,
      generalMessage: hasUnmatched ? GENERIC_SUBMIT_ERROR : null,
    };
  }

  if (typeof status === 'number' && status >= 500) {
    return { fieldErrors: null, generalMessage: SERVER_SUBMIT_ERROR };
  }

  return { fieldErrors: null, generalMessage: GENERIC_SUBMIT_ERROR };
}

interface SignupFormFieldsProps {
  form: UseFormReturn<SignupFormValues>;
  generalMessage: string | null;
  isSubmitting: boolean;
}

/**
 * A separate component (not `Form`'s render-prop function directly): that way
 * the `useEffect`/`useRef` below live in THIS component's fibre and not in
 * `Form`'s — `Form` only returns what `children` produces, and what `children`
 * produces here is an element with hooks of its own, not hooks running
 * "borrowed" inside `Form`'s render.
 */
function SignupFormFields({
  form,
  generalMessage,
  isSubmitting,
}: SignupFormFieldsProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-stack">
      {generalMessage ? (
        <Callout tone="danger" title="No se pudo completar el registro">
          {generalMessage}
        </Callout>
      ) : null}

      <FormField name="organizationName" label="Nombre de la organización" required>
        {(field) => (
          <Input
            {...form.register('organizationName')}
            {...field}
            type="text"
            autoComplete="organization"
            placeholder="Comercializadora Ejemplo SpA"
          />
        )}
      </FormField>

      <FormField name="email" label="Correo del administrador" required>
        {(field) => (
          <Input
            {...form.register('email')}
            {...field}
            type="email"
            autoComplete="email"
            placeholder="admin@empresa.cl"
          />
        )}
      </FormField>

      <FormField
        name="password"
        label="Contraseña (opcional)"
        description="Si la dejas en blanco, Factit crea una para tu cuenta SSO y puedes cambiarla más tarde iniciando sesión."
      >
        {(field) => (
          <Input {...form.register('password')} {...field} type="password" autoComplete="new-password" />
        )}
      </FormField>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Creando organización…' : 'Crear organización'}
      </Button>
    </div>
  );
}

/**
 * ## Sign-up ends inside the product, not on a login screen
 *
 * This page used to answer a successful submit by revealing the tenant's API
 * key through `SecretRevealOnce` and then sending the person to `/login`. Two
 * things were wrong with that. The person who had just created an account
 * finished signed *out*, holding a secret they had not asked for; and when the
 * provider's user token is unset the backend answers
 * `ssoUserProvisioned: false`, which means that login screen would never let
 * them in and the form had no way to say so.
 *
 * The backend now sets the session cookie as part of `POST /api/signup`, so
 * the only thing left here is to walk into the app. The API key is still
 * minted and still returned — integrators depend on it — it simply is not a
 * human's first artefact any more; it lives at `/account/api-keys`.
 *
 * The one case that still stops to talk is `ssoUserProvisioned: false`: the
 * session works, so the person gets in, but they need to know their SSO
 * account was not created before the two hours run out.
 */
export function SignupPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [signup, { data, error, isLoading }] = useSignupControllerSignupMutation();

  const { fieldErrors, generalMessage } = React.useMemo(
    () => mapSignupSubmitError(error),
    [error],
  );

  async function handleSubmit(values: SignupFormValues): Promise<void> {
    await signup({
      signupDto: {
        organizationName: values.organizationName,
        email: values.email,
        password: values.password.length > 0 ? values.password : undefined,
      },
    });
  }

  // The session is already set by the time `data` arrives, and the backend has
  // mailed the code, so the next screen is the one that asks for it. It is not
  // a gate — it offers «lo hago después» — but it is where the person is
  // expected to go next.
  React.useEffect(() => {
    if (data && data.ssoUserProvisioned) {
      navigate('/verify-email', { replace: true });
    }
  }, [data, navigate]);

  if (data && !data.ssoUserProvisioned) {
    return (
      <CenteredCardLayout
        title="Tu organización está creada"
        description="Ya puedes entrar, pero falta un paso para que puedas volver a iniciar sesión más adelante."
        brand={TCIT_BRAND}
      >
        <div className="flex flex-col gap-stack">
          <Callout tone="warning" title="No pudimos crear tu cuenta de acceso">
            Tu sesión dura dos horas. Antes de que termine, escríbenos para crear tu cuenta y no
            quedarte fuera.
          </Callout>
          <Button type="button" className="w-full" onClick={() => navigate('/verify-email', { replace: true })}>
            Continuar
          </Button>
        </div>
      </CenteredCardLayout>
    );
  }

  return (
    <CenteredCardLayout
      title="Crea tu organización en Factit"
      description="Vas a poder emitir documentos apenas termines."
      brand={TCIT_BRAND}
      footer={
        <p className="text-body-s text-fg-muted">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="font-medium text-action hover:text-action-hover">
            Inicia sesión
          </Link>
        </p>
      }
    >
      <Form
        schema={signupSchema}
        defaultValues={SIGNUP_DEFAULT_VALUES}
        onSubmit={handleSubmit}
        serverErrors={fieldErrors}
      >
        {(form) => (
          <SignupFormFields form={form} generalMessage={generalMessage} isSubmitting={isLoading} />
        )}
      </Form>
    </CenteredCardLayout>
  );
}
